import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Parameters} from 'platform/components';
import {Box, Grid, HStack, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber, useFormatPercentage} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {filter, isNotNil} from 'ramda';
import {floor} from 'ramda-adjunct';

import {SourcingVehicleResponseBody, useSourcingVehicleDetailQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, TestIdProps, isNotNilOrEmpty, suffixTestId} from 'shared/web';

import {getFirstScrapedPrice} from '../../../../../../../components/PriceChangesPopper/getFirstScrapedPrice';
import {VehicleImage} from '../../../../../../../components/VehicleImage/VehicleImage';
import {DEFAULT_CURRENCY_CODE} from '../../../../../../../constants/DEFAULT_CURRENCY_CODE';
import {MAP_TOOLTIP_CARD_HEIGHT_IN_STEPS} from '../../../../../../../constants/MAP_TOOLTIP_CARD_HEIGHT_IN_STEPS';
import {useSourcingCurrency} from '../../../../../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../../../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../../../../../hooks/useSourcingTargetCountry';
import {isUnifiedSourcingEnabled} from '../../../../../../../utils/isUnifiedSourcingEnabled';
import {getDifference} from '../utils/getDifference';
import {ComparedValue} from './ComparedValue';

interface SourcingVehicleMapTooltipCardProps extends TestIdProps {
  externalId: string | Nullish;
  comparisonVehicle?: SourcingVehicleResponseBody | Nullish;
}

/**
 * The vehicle card used by the Sourcing price map. Keeping this as a component
 * makes every geographic map tooltip render the very same card and loading state.
 */
// eslint-disable-next-line complexity -- Pre-existing violation
export function SourcingVehicleMapTooltipCard(props: SourcingVehicleMapTooltipCardProps) {
  const hasUnifiedSurfaces = isUnifiedSourcingEnabled();
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatPercentage = useFormatPercentage();
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code;
  const pricingStrategy = useSourcingPricingStrategy();
  const {
    data: vehicle,
    isFetching,
    isError,
  } = useSourcingVehicleDetailQuery(
    isNotNil(props.externalId)
      ? {externalId: props.externalId, currency, buyerCountry: country, pricingStrategy}
      : skipToken
  );

  const vehicleName = match(vehicle?.sourcingVehicle?.adTitle)
    .with(Pattern.nonNullable, () => vehicle?.sourcingVehicle?.adTitle)
    .otherwise(
      () =>
        `${vehicle?.sourcingVehicle?.make?.translation} ${vehicle?.sourcingVehicle?.model?.translation}`
    );
  const vehicleDotListItems: string[] = filter(isNotNilOrEmpty, [
    vehicle?.sourcingVehicle?.transmission?.translation,
    vehicle?.sourcingVehicle?.fuelType?.translation,
    isNotNil(vehicle?.sourcingVehicle?.power)
      ? `${vehicle.sourcingVehicle.power} ${i18n.t('general.metric.kW')}`
      : null,
    vehicle?.sourcingVehicle?.driveType?.translation,
    vehicle?.sourcingVehicle?.carStyle?.translation,
  ]);
  const sourceVehicle = props.comparisonVehicle;
  const yearDifference = sourceVehicle
    ? getDifference(vehicle?.sourcingVehicle?.year, sourceVehicle.year)
    : null;
  const featureScoreDifference = sourceVehicle
    ? getDifference(vehicle?.sourcingVehicle?.featureScore ?? 0, sourceVehicle.featureScore)
    : null;
  const mileageDifference = sourceVehicle
    ? getDifference(vehicle?.sourcingVehicle?.mileage, sourceVehicle.mileage)
    : null;
  const vehiclePrice = vehicle?.sourcingVehicle?.price?.withVat ?? 0;
  const priceDifference = sourceVehicle
    ? getDifference(vehiclePrice, sourceVehicle.price?.withVat ?? 0)
    : null;
  const vehicleCurrency = vehicle?.sourcingVehicle?.price?.currency?.key ?? DEFAULT_CURRENCY_CODE;
  const advertisementDotListItems: string[] = filter(isNotNilOrEmpty, [
    i18n.t('entity.vehicle.labels.nDaysInAdvertisement', {
      count: vehicle?.sourcingVehicle?.daysOnStock,
    }),
    vehicle?.sourcingVehicle?.seller?.type?.translation,
  ]);

  return (
    <Box
      backgroundColor="general.white"
      borderRadius="medium"
      boxShadow="elevation_3"
      border="1px solid"
      borderColor="palettes.neutral.40.100"
      overflow="hidden"
      minWidth={175}
    >
      <DataStatus
        isLoading={isFetching}
        isError={isError}
        minHeight={MAP_TOOLTIP_CARD_HEIGHT_IN_STEPS}
      >
        <HStack height="100%">
          <VehicleImage
            vehicle={vehicle}
            width={45}
            height={MAP_TOOLTIP_CARD_HEIGHT_IN_STEPS}
            hasUnifiedSurfaces={hasUnifiedSurfaces}
          />
          <Box padding={4} paddingBottom={2} height={MAP_TOOLTIP_CARD_HEIGHT_IN_STEPS}>
            <VStack justify="space-between" height="100%">
              <Box flexGrow={1}>
                <Text size="small" alternative>
                  {vehicleName}
                </Text>
                <Parameters
                  parameters={vehicleDotListItems}
                  color="secondary"
                  size="xSmall"
                  data-testid={suffixTestId('vehicleParameters', props)}
                />
                <Space vertical={1} />
                <Grid spacing={4} columns={4}>
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.year ?? 0}
                    difference={yearDifference}
                    data-testid={suffixTestId('year', props)}
                  />
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.featureScore ?? 0}
                    difference={featureScoreDifference}
                    rankValue={floor((vehicle?.sourcingVehicle?.featureScore ?? 0) * 100)}
                    formatter={(value) => formatPercentage(value, 'down')}
                    data-testid={suffixTestId('featureScore', props)}
                  />
                  <ComparedValue
                    value={vehicle?.sourcingVehicle?.mileage ?? 0}
                    difference={mileageDifference}
                    formatter={(value) => `${formatNumber(value)} ${i18n.t('general.metric.km')}`}
                    isReverseColor
                    data-testid={suffixTestId('mileage', props)}
                  />
                  <ComparedValue
                    value={vehiclePrice}
                    difference={priceDifference}
                    firstScrape={getFirstScrapedPrice(vehicle?.sourcingVehicle, null)}
                    formatter={(value) => formatCurrency(value, vehicleCurrency)}
                    firstScrapeFormatter={(value) => formatCurrency(value, vehicleCurrency)}
                    isReverseColor
                    isAlignEnd
                    data-testid={suffixTestId('price', props)}
                  />
                </Grid>
              </Box>
              <Parameters
                parameters={advertisementDotListItems}
                color="secondary"
                size="xSmall"
                data-testid={suffixTestId('advertisementVehicleParameters', props)}
              />
            </VStack>
          </Box>
        </HStack>
      </DataStatus>
    </Box>
  );
}
